import { useState } from "react";
import { Play, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";

/**
 * VideoMarquee — continuous left-to-right marquee of mixed-format video
 * testimonial cards (16:9 YouTube + 9:16 Reels). Cards are clickable: opens
 * a Dialog with the actual YouTube embed.
 */

// All ratios are width:height. Cards keep height constant and width derives
// from aspect; the marquee row has a uniform visual height.
const VIDEOS = [
  {
    id: "km-pre-launch",
    format: "youtube",
    aspect: "16/9",
    thumb:
      "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=900&q=80",
    name: "KM Builders",
    role: "Director — Real Estate",
    youtubeId: "dQw4w9WgXcQ",
  },
  {
    id: "ora-reel-1",
    format: "reel",
    aspect: "9/16",
    thumb:
      "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&w=600&q=80",
    name: "Ora Makers",
    role: "Brand Repositioning Reel",
    youtubeId: "dQw4w9WgXcQ",
  },
  {
    id: "zenith-cfo",
    format: "youtube",
    aspect: "16/9",
    thumb:
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=900&q=80",
    name: "Zenith House",
    role: "CFO Interview",
    youtubeId: "dQw4w9WgXcQ",
  },
  {
    id: "vista-reel",
    format: "reel",
    aspect: "9/16",
    thumb:
      "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=600&q=80",
    name: "Vista Heights",
    role: "Site Walkthrough Reel",
    youtubeId: "dQw4w9WgXcQ",
  },
  {
    id: "skyline-launch",
    format: "youtube",
    aspect: "16/9",
    thumb:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=80",
    name: "Skyline Estates",
    role: "Launch Campaign Recap",
    youtubeId: "dQw4w9WgXcQ",
  },
  {
    id: "halo-reel",
    format: "reel",
    aspect: "9/16",
    thumb:
      "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=600&q=80",
    name: "Halo Living",
    role: "Founder Reel",
    youtubeId: "dQw4w9WgXcQ",
  },
  {
    id: "axis-developer",
    format: "youtube",
    aspect: "16/9",
    thumb:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80",
    name: "Axis Realty",
    role: "Developer Testimonial",
    youtubeId: "dQw4w9WgXcQ",
  },
  {
    id: "ember-brand",
    format: "reel",
    aspect: "9/16",
    thumb:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=600&q=80",
    name: "Ember Studio",
    role: "Brand Film Reel",
    youtubeId: "dQw4w9WgXcQ",
  },
];

function VideoCard({ v, onPlay }) {
  const widthClass =
    v.aspect === "9/16"
      ? "w-[200px] sm:w-[240px]"
      : "w-[360px] sm:w-[440px]";
  return (
    <button
      type="button"
      onClick={() => onPlay(v)}
      data-testid={`video-card-${v.id}`}
      className={`group relative shrink-0 ${widthClass} rounded-2xl overflow-hidden bg-black border border-black/10 transition-all duration-500 hover:border-[#2770FD] hover:shadow-[0_18px_45px_-15px_rgba(39,112,253,0.55)] hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-[#2770FD]/40`}
      style={{ aspectRatio: v.aspect }}
    >
      <img
        src={v.thumb}
        alt={`${v.name} video testimonial — ${v.role}`}
        loading="lazy"
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-[1200ms] ease-out group-hover:scale-105"
      />
      {/* Dark gradient for legibility */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10"
      />

      {/* Format badge */}
      <span className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full bg-white/95 text-[10px] uppercase tracking-[0.22em] text-[#2770FD] font-semibold">
        {v.format === "reel" ? "Reel" : "YouTube"}
      </span>

      {/* Royal blue play button overlay */}
      <span
        aria-hidden
        className="absolute inset-0 flex items-center justify-center"
      >
        <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#2770FD] bg-white/10 backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
          <span className="absolute inset-0 rounded-full bg-[#2770FD]/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
          <Play
            className="relative h-6 w-6 text-white"
            fill="#2770FD"
            stroke="#2770FD"
          />
        </span>
      </span>

      {/* Caption */}
      <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
        <p className="font-display text-white text-base sm:text-lg leading-tight">
          {v.name}
        </p>
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/75 mt-1">
          {v.role}
        </p>
      </div>
    </button>
  );
}

export default function VideoMarquee() {
  const [active, setActive] = useState(null);

  // Duplicate the list so the marquee loop is seamless
  const loop = [...VIDEOS, ...VIDEOS];

  return (
    <div
      data-testid="video-marquee"
      className="relative w-full overflow-hidden py-4"
    >
      <div className="marquee-ltr flex w-max gap-5 sm:gap-6 items-end py-6">
        {loop.map((v, i) => (
          <VideoCard
            key={`${v.id}-${i}`}
            v={{ ...v, id: i < VIDEOS.length ? v.id : `${v.id}-dup` }}
            onPlay={setActive}
          />
        ))}
      </div>

      {/* Edge fades to white */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-white to-transparent" />

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="video-player-dialog"
          className="max-w-3xl bg-black border-black/10 p-0 overflow-hidden"
        >
          {active && (
            <div
              className="relative w-full"
              style={{ aspectRatio: active.aspect }}
            >
              <iframe
                src={`https://www.youtube.com/embed/${active.youtubeId}?autoplay=1&rel=0`}
                title={`${active.name} — ${active.role}`}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
              <button
                onClick={() => setActive(null)}
                aria-label="Close video"
                className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/70 backdrop-blur text-white hover:bg-black"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
